import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableDropdownProps {
  name: string;
  value: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  required?: boolean;
  searchPlaceholder?: string;
  style?: React.CSSProperties;
  variant?: 'light' | 'dark';
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  name,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  className = "",
  required = false,
  searchPlaceholder = "Search...",
  style = {},
  variant = 'light'
}) => {
  const dark = variant === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) && !option.disabled
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both the dropdown button and the portaled menu
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // For portaled content, we need to check if the click is on the portaled dropdown
        const portaledDropdown = document.querySelector('[data-dropdown-portal]');
        if (!portaledDropdown || !portaledDropdown.contains(target)) {
          setIsOpen(false);
          setFocusedIndex(-1);
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(option => option.value === value);

  const handleOptionClick = useCallback((optionValue: string) => {
    const selectedOpt = options.find(opt => opt.value === optionValue);
    if (selectedOpt && !selectedOpt.disabled) {
      onChange({
        target: {
          name,
          value: optionValue
        }
      });
      setIsOpen(false);
      setFocusedIndex(-1);
      setSearchTerm('');
    }
  }, [name, onChange, options]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          setIsOpen(false);
          setFocusedIndex(-1);
          setSearchTerm('');
          buttonRef.current?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(prev => {
            const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
            return newIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(prev => {
            const newIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
            return newIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
            handleOptionClick(filteredOptions[focusedIndex].value);
          }
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, focusedIndex, filteredOptions, handleOptionClick]);

  // Calculate optimal dropdown position and portal position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = Math.min(filteredOptions.length * 48 + 60, 300); // +60 for search input
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      // Set portal position based on button position
      setPortalPosition({
        top: spaceBelow < dropdownHeight && spaceAbove > dropdownHeight 
          ? buttonRect.top - dropdownHeight 
          : buttonRect.bottom,
        left: buttonRect.left,
        width: buttonRect.width
      });

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen, filteredOptions.length]);

  // Update portal position on scroll/resize
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = Math.min(filteredOptions.length * 48 + 60, 300);
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        setPortalPosition({
          top: spaceBelow < dropdownHeight && spaceAbove > dropdownHeight 
            ? buttonRect.top - dropdownHeight 
            : buttonRect.bottom,
          left: buttonRect.left,
          width: buttonRect.width
        });

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          setDropdownPosition('top');
        } else {
          setDropdownPosition('bottom');
        }
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, filteredOptions.length]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setFocusedIndex(0); // Reset focus to first filtered option
  };

  // Render dropdown menu
  const renderDropdownMenu = () => {
    if (!isOpen) return null;

    return (
      <div 
        data-dropdown-portal
        className={`rounded-xl overflow-hidden animate-fade-in-up ${dark ? 'bg-slate-900 border border-white/10 shadow-[0_16px_40px_rgba(0,0,0,0.4)]' : 'bg-karvlo-white border-2 border-karvlo-gray/30 shadow-[0_16px_32px_rgba(0,0,0,0.15)]'}`}
        style={{
          position: 'fixed',
          top: portalPosition.top,
          left: portalPosition.left,
          width: portalPosition.width,
          maxHeight: '300px',
          zIndex: 999999,
          transformOrigin: dropdownPosition === 'top' ? 'bottom' : 'top'
        }}
      >
        {/* Search Input */}
        <div className={`p-3 border-b ${dark ? 'border-white/10' : 'border-karvlo-gray/20'}`}>
          <div className="relative">
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
              className={`w-full px-3 py-2 rounded-lg focus:outline-none transition-all duration-200 text-sm pr-8 ${dark ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-cyan-500/50' : 'border border-karvlo-gray/30 focus:border-karvlo-blue bg-karvlo-white'}`}
            />
            <svg
              className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 ${dark ? 'text-slate-500' : 'text-karvlo-gray'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Options List */}
        <div className="max-h-52 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className={`px-4 py-3 text-center text-sm ${dark ? 'text-slate-500' : 'text-karvlo-gray'}`}>
              No options found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isFocused = index === focusedIndex;
              
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full px-4 py-3 text-left transition-all duration-200 cursor-pointer ${
                    dark ? 'text-slate-200 hover:bg-white/10' : 'text-karvlo-navy hover:bg-karvlo-sky hover:shadow-[0_2px_0_rgba(125,211,252,0.2)]'
                  } ${
                    option.value === value ? (dark ? 'bg-cyan-500/15 text-white font-semibold' : 'bg-karvlo-sky text-karvlo-navy font-semibold') : ''
                  } ${
                    isFocused ? (dark ? 'bg-cyan-500/20 ring-1 ring-cyan-500/40' : 'bg-karvlo-blue/20 ring-2 ring-karvlo-blue/50') : ''
                  }`}
                  onClick={() => handleOptionClick(option.value)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {option.label}
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef} style={style}>
        <button
          ref={buttonRef}
          type="button"
          className={`w-full px-4 py-3 rounded-xl focus:outline-none transition-all duration-300 text-left flex items-center justify-between ${
            dark
              ? `bg-white/5 border border-white/10 focus:border-cyan-500/50 ${isOpen ? 'border-cyan-500/50 shadow-[0_0_15px_rgba(56,189,248,0.15)]' : ''}`
              : `border-2 border-karvlo-gray/30 focus:border-karvlo-blue focus:shadow-[0_0_15px_rgba(125,211,252,0.3)] bg-karvlo-white/80 ${isOpen ? 'border-karvlo-blue shadow-[0_0_15px_rgba(125,211,252,0.3)]' : ''}`
          }`}
          onClick={() => {
            setIsOpen(!isOpen);
            setFocusedIndex(-1);
            setSearchTerm('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
              setFocusedIndex(-1);
              setSearchTerm('');
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={selectedOption ? (dark ? 'text-white' : 'text-karvlo-navy') : (dark ? 'text-slate-500' : 'text-karvlo-gray')}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`w-5 h-5 ${dark ? 'text-slate-500' : 'text-karvlo-gray'} transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      
      {/* Portal the dropdown menu to document.body */}
      {createPortal(renderDropdownMenu(), document.body)}
    </>
  );
};

export default SearchableDropdown;
